import { useForm } from '@tanstack/react-form';
import { log } from 'evlog/client';
import { ArrowLeft, Dot } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { z } from 'zod';

import {
  Button,
  CardContent,
  InputOTP,
  InputOTPGroup,
  InputOTPSlot,
} from '@virtual-care/shadcn';
import {
  AuthLayout,
  FieldMessage,
  FormMessage,
  errorToFormIssue,
} from '@virtual-care/ui';

import { ResendButton } from '../components/resend-button.tsx';
import {
  confirmCode,
  getIdentity,
  sendOtp,
  useCanConfirmCode,
} from '../firebase.tsx';

const schema = z.object({
  code: z.string().length(6, { message: 'shared.validation.otpLength' }),
});

export default function PhoneVerification() {
  const { canConfirm, phoneNumber } = useCanConfirmCode();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const otpInputRef = useRef<any>(null);

  const form = useForm({
    defaultValues: {
      code: '',
    },
    validators: {
      onSubmit: schema,
      onSubmitAsync: async ({ value }) => {
        try {
          await confirmCode(value.code);
          await getIdentity();
          await navigate('/');
          return void 0;
        } catch (error) {
          return errorToFormIssue(error);
        }
      },
    },
  });

  useEffect(() => {
    if (otpInputRef.current) {
      otpInputRef.current.focus();
    }
  }, []);

  const otpCountdown = useOtpCountdown();

  return (
    <AuthLayout
      title={
        <>
          {t('auth.verifyPhone.title')} {otpCountdown}
        </>
      }
      subtitle={t('auth.verifyPhone.subtitle')}
      center={true}
    >
      <CardContent className="space-y-6 px-0">
        <form
          onSubmit={async (e) => {
            e.preventDefault();
            e.stopPropagation();
            await form.handleSubmit();
          }}
          className="space-y-6"
        >
          <form.Field
            name="code"
            children={(field) => (
              <div className="space-y-4">
                <div className="flex justify-center" dir="ltr">
                  <InputOTP
                    ref={otpInputRef}
                    maxLength={6}
                    value={field.state.value}
                    onChange={field.handleChange}
                  >
                    <InputOTPGroup>
                      <InputOTPSlot index={0} />
                      <InputOTPSlot index={1} />
                      <InputOTPSlot index={2} />
                    </InputOTPGroup>
                    <div role="separator">
                      <Dot />
                    </div>
                    <InputOTPGroup>
                      <InputOTPSlot index={3} />
                      <InputOTPSlot index={4} />
                      <InputOTPSlot index={5} />
                    </InputOTPGroup>
                  </InputOTP>
                </div>
                <FieldMessage errors={field.state.meta.errors} />
              </div>
            )}
          />

          <div className="flex items-center justify-center gap-x-1">
            <p className="text-muted-foreground text-sm">
              {t('auth.verifyPhone.didntReceive')}
            </p>
            <ResendButton
              id="resend-otp-btn"
              onResend={async () => {
                if (!phoneNumber) {
                  log.warn({ action: 'resend_otp', reason: 'no_phone_number' });
                  return;
                }
                await sendOtp(phoneNumber);
              }}
            />
          </div>
          <form.Subscribe
            selector={(state) => [
              state.canSubmit,
              state.isSubmitting,
              state.isSubmitSuccessful,
            ]}
            children={([canSubmit, isSubmitting, isSubmitSuccessful]) => (
              <Button
                type="submit"
                disabled={
                  !canSubmit ||
                  isSubmitting ||
                  !canConfirm ||
                  isSubmitSuccessful // one submit only
                }
                className="w-full cursor-pointer"
              >
                {isSubmitting ? (
                  t('auth.verifyPhone.verifying')
                ) : (
                  <>
                    {t('auth.verifyPhone.submit')}
                    <ArrowLeft className="mx-2 h-4 w-4 rtl:rotate-180" />
                  </>
                )}
              </Button>
            )}
          />

          <form.Subscribe
            selector={(state) => state.errors}
            children={(errors) => <FormMessage errors={errors} />}
          />
        </form>
      </CardContent>
    </AuthLayout>
  );
}

// #region useOtpCountdown
const OTP_EXPIRY_SECONDS = 5 * 60; // 5 minutes

function useOtpCountdown() {
  const { t } = useTranslation();
  const [remaining, setRemaining] = useState(() => {
    const sentAt = sessionStorage.getItem('otpSentAt');
    if (!sentAt) return 0;
    const elapsed = Math.floor((Date.now() - Number(sentAt)) / 1000);
    return Math.max(0, OTP_EXPIRY_SECONDS - elapsed);
  });

  useEffect(() => {
    if (remaining <= 0) return;
    const interval = setInterval(() => setRemaining((r) => r - 1), 1000);
    return () => clearInterval(interval);
  }, [remaining]);

  const mins = Math.floor(remaining / 60);
  const secs = remaining % 60;

  if (remaining <= 0) {
    return (
      <span className="text-destructive">{t('auth.verifyPhone.expired')}</span>
    );
  }

  return `${mins}:${secs.toString().padStart(2, '0')}`;
}
// #endregion
